/**
 * WebSocket auth helpers for /ws/mission-control, /ws/tasks, collection channels, etc.
 *
 * Server handshake (ani_api auth middleware) accepts, in order:
 * 1. WorkOS session cookie (`workos_auth` in SessionMiddleware) when WorkOS is enabled
 * 2. Bearer JWT via `?token=` query param or `Authorization: Bearer` header
 *
 * Public endpoints: none — all WebSocket routes require authentication.
 *
 * Browser clients: append `?token=` only when a user JWT exists in localStorage.
 * WorkOS / SSO users rely on session cookies (withCredentials is implicit for WebSocket).
 */

/** Policy violation (auth rejected) — do not reconnect. */
export const WS_CLOSE_POLICY_VIOLATION = 1008;

/** Abnormal closure — common when the server rejects a handshake before accept. */
export const WS_CLOSE_ABNORMAL = 1006;

/** Normal close — do not reconnect. */
export const WS_CLOSE_NORMAL = 1000;

/** Default cap for collection-channel reconnect attempts (see WebSocketManager). */
export const WS_DEFAULT_MAX_RECONNECT_ATTEMPTS = 5;

/** Close reason from API when WorkOS session refresh fails (AuthMiddleware). */
export const WORKOS_SESSION_EXPIRED_WS_REASON =
  "Session expired or invalid - please log in again";

const WS_AUTH_FAILURE_REASONS = new Set([
  "No token provided",
  "Invalid token",
  "Unauthorized",
  "User not found - please contact support",
  "Authentication error - please try logging in again",
  WORKOS_SESSION_EXPIRED_WS_REASON,
]);

export type WebSocketAuthOptions = {
  /**
   * Optional bearer token when localStorage has no `auth_token`.
   * Use only for explicit per-user JWTs — not API keys.
   */
  fallbackToken?: string | null;
};

/** Read JWT from localStorage auth_data (same source as API client). */
export function getBearerTokenFromAuthData(): string | null {
  try {
    const authData = localStorage.getItem("auth_data");
    if (!authData) return null;
    const parsed = JSON.parse(authData) as { auth_token?: string };
    const token = parsed?.auth_token;
    return typeof token === "string" && token.length > 0 ? token : null;
  } catch {
    return null;
  }
}

/**
 * Resolve the bearer token to send on a WebSocket handshake, if any.
 * Precedence: localStorage JWT, then optional fallback.
 */
export function resolveWebSocketBearerToken(
  fallbackToken?: string | null,
): string | null {
  const fromStorage = getBearerTokenFromAuthData();
  if (fromStorage) return fromStorage;
  if (typeof fallbackToken === "string" && fallbackToken.length > 0) {
    return fallbackToken;
  }
  return null;
}

/**
 * Whether the client can attempt a WebSocket handshake.
 * Legacy JWT users need only a stored bearer token; WorkOS users also need a
 * server-verified session (`webSocketAuthReady` from AuthContext).
 */
export function canAttemptWebSocketAuth(
  isAuthenticated: boolean,
  webSocketAuthReady: boolean = false,
): boolean {
  if (getBearerTokenFromAuthData() !== null) return true;
  return isAuthenticated && webSocketAuthReady;
}

/**
 * Build a WebSocket URL, appending `?token=` / `&token=` when a bearer JWT is available.
 * When no token is resolved, returns the URL unchanged so WorkOS session cookies apply.
 */
export function buildWebSocketUrl(
  wsUrl: string,
  options?: WebSocketAuthOptions,
): string {
  const token = resolveWebSocketBearerToken(options?.fallbackToken);
  if (!token) return wsUrl;
  const separator = wsUrl.includes("?") ? "&" : "?";
  return `${wsUrl}${separator}token=${encodeURIComponent(token)}`;
}

/** @deprecated Prefer `buildWebSocketUrl` — kept for existing call sites. */
export function appendBearerTokenToWsUrl(wsUrl: string): string {
  return buildWebSocketUrl(wsUrl);
}

/** True when the server rejected the WebSocket handshake for auth reasons. */
export function isWebSocketAuthFailureClose(
  closeCode: number,
  reason: string,
): boolean {
  const trimmed = reason.trim();
  if (closeCode === WS_CLOSE_POLICY_VIOLATION) {
    return trimmed.length === 0 || WS_AUTH_FAILURE_REASONS.has(trimmed);
  }
  if (closeCode === WS_CLOSE_ABNORMAL && trimmed.length > 0) {
    return WS_AUTH_FAILURE_REASONS.has(trimmed);
  }
  return false;
}

/**
 * Whether to schedule reconnect after onclose.
 * Handshake failures (never opened) and auth rejections are terminal.
 * Note: Standard browser WebSocket implementations do not expose close reasons
 * on abnormal closures (1006); options.hadOpened handles handshake failure detection.
 */
export function shouldReconnectAfterWebSocketClose(
  closeCode: number,
  reason = "",
  options?: { hadOpened?: boolean },
): boolean {
  if (options?.hadOpened === false) {
    return false;
  }
  if (isWebSocketAuthFailureClose(closeCode, reason)) {
    return false;
  }
  return closeCode !== WS_CLOSE_POLICY_VIOLATION && closeCode !== WS_CLOSE_NORMAL;
}

/** True when the server rejected the handshake because the WorkOS session ended. */
export function isWorkOsSessionExpiredWebSocketClose(
  closeCode: number,
  reason: string,
): boolean {
  return closeCode === WS_CLOSE_POLICY_VIOLATION && reason === WORKOS_SESSION_EXPIRED_WS_REASON;
}
